import { useEffect, useRef, useState } from 'react';
import { useClienteContext } from '../../../../contexts/ClienteContext';
import { useEmpresaContext } from '../../../../contexts/EmpresaContext';
import axiosAuth from '../../../../services/axiosAuth';
import { generarPdfRemitosPendientesFacturar } from '../../../../services/generarPdfRemitosPendientesFacturar';
import imagenfondo from '../../../../images/logo-sur.jpg';
import imagenFondoIntegral from '../../../../images/logo-nuevo-srl.jpg';
import { ChevronUp, ChevronDown, Printer, AlertCircle, Loader2 } from 'lucide-react';



const COLUMNAS = [
    { label: 'Fecha', title: '' },
    { label: 'E/S', title: 'Entrada / Salida' },
    { label: 'N° Comprobante', title: '' },
    { label: 'Dep', title: 'Depósito' },
    { label: 'Código', title: '' },
    { label: 'Descripción', title: '' },
    { label: '% IVA', title: '' },
    { label: 'Precio', title: '' },
    { label: 'Cantidad', title: '' },
    { label: 'Moneda', title: '' },
    { label: 'Comentario', title: '' },
];

export default function TablaPendienteFacturar() {
    const sucursal = localStorage.getItem('sucursal');
    const fondo = sucursal === 'sur' ? imagenfondo : imagenFondoIntegral;

    const { getCliente } = useClienteContext();
    const data = getCliente();
    const codigo = data.codigoCliente;
    const { getEmpresa } = useEmpresaContext();
    const empresa = getEmpresa();

    const [movPendientes, setMovPendientes] = useState([]);
    const [mensajeError, setMensajeError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const tableContainerRef = useRef(null);

    const remitosPendientesFacturar = movPendientes?.filter(rem => rem?.GenfacturaRemito == '1');

    useEffect(() => { fetchData(); }, [codigo]);

    useEffect(() => {
        if (tableContainerRef.current) {
            const { scrollHeight, clientHeight } = tableContainerRef.current;
            setShowScrollButtons(scrollHeight > clientHeight);
            tableContainerRef.current.scrollTop = scrollHeight;
        }
    }, [movPendientes]);



    const fetchData = async () => {
        if (!codigo || !sucursal) { setMensajeError('Código de cliente o sucursal no definido'); return; }
        setLoading(true);
        setMensajeError('');
        try {
            // Request 1 — primeros 100 al instante
            const response = await axiosAuth.get(`/movstock/${sucursal}/pendientefacturar/${codigo}?limit=50`);
            setMovPendientes(response?.data);
            setLoading(false);

            // Request 2 — resto en background
            try {
                const total = await axiosAuth.get(`/movstock/${sucursal}/pendientefacturar/${codigo}`);
                if (total?.data?.length > response?.data?.length) {
                    setMovPendientes(total?.data);
                }
            } catch (e) {
                console.warn('No se pudieron cargar todos los registros:', e.message);
            }

        } catch (error) {
            setMensajeError(error.response?.data?.message);
            setLoading(false);
        }
    };

    const handleImprimirClick = () => generarPdfRemitosPendientesFacturar(remitosPendientesFacturar, data, empresa);

    return (
        <div className="flex flex-col h-full bg-white">

            {/* HEADER */}
            <div className="flex-shrink-0 bg-[#050f08] border-b border-gray-700">
                <div className="px-4 py-1 flex flex-col items-center gap-1">
                    <p className="text-white font-bold text-xs tracking-widest uppercase text-center">
                        Remitos Pendientes de Facturar
                    </p>
                    <p className="text-gray-400 text-[10px] uppercase tracking-wider">
                        Actualizado: {new Date(empresa?.updatedAt)?.toLocaleString('es-ES', { hour12: false })}
                    </p>
                </div>
            </div>

            {/* BOTÓN IMPRIMIR */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex justify-center">
                <button
                    onClick={handleImprimirClick}
                    disabled={!remitosPendientesFacturar?.length}
                    className="flex items-center gap-2 bg-gray-900 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-white text-[10px] font-black uppercase tracking-widest px-5 py-2 rounded-xl transition-all"
                >
                    <Printer size={14} />
                    Imprimir
                </button>
            </div>

            {/* TABLA */}
            <div className="overflow-auto relative flex-1" ref={tableContainerRef}
                style={{
                    backgroundImage: `url(${fondo})`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    // maxHeight: 'calc(100vh - 220px)'
                }}>

                {loading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 size={28} className="text-gray-400 animate-spin" />
                            <span className="text-gray-400 text-xs uppercase tracking-widest font-bold">Cargando...</span>
                        </div>
                    </div>
                )}

                {!loading && mensajeError && (
                    <div className="m-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                        <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                        <p className="text-red-600 text-xs font-black uppercase tracking-wider">{mensajeError}</p>
                    </div>
                )}

                <table className="w-full border-collapse text-xs">
                    <thead>
                        <tr className="bg-gray-800 sticky top-0 z-10">
                            {COLUMNAS.map((col, i) => (
                                <th key={i} title={col.title}
                                    className="sticky top-0 px-2 py-1 border-r border-gray-700 text-white/80 font-bold uppercase tracking-wider whitespace-nowrap text-center last:border-r-0">
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {remitosPendientesFacturar?.map((mov, index) => (
                            <tr key={index} className="bg-white/85 hover:bg-blue-200 transition-colors border-b border-gray-200 text-center">
                                <td className="px-2 py-1 border-r border-gray-100 whitespace-nowrap text-gray-700">{mov?.Fecha}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-gray-600">{mov?.Tipomov}</td>
                                <td className="px-2 py-1 border-r border-gray-100 whitespace-nowrap text-right text-gray-700">{mov?.Talonario}-{mov?.Nrocomprobante}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-gray-600">{mov?.Coddeposito}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-gray-600">{mov?.Codarticulo}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-left text-gray-700">{mov?.Descarticulo}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-right text-gray-600">{mov?.Poriva}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-right text-gray-700 font-semibold">
                                    {empresa?.mostrar_precio === 'SI' ? mov?.Precioarticulo?.toFixed(2) : '0'}
                                </td>
                                <td className="px-2 py-1 border-r border-gray-100 text-right text-gray-700">{mov?.Cantidad}</td>
                                <td className="px-2 py-1 border-r border-gray-100 text-center">
                                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${mov?.Dolar == '1' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                        {mov?.Dolar == '1' ? 'USD' : 'ARS'}
                                    </span>
                                </td>
                                <td className="px-2 py-1 border-r border-gray-100 text-left text-gray-500 max-w-[120px] truncate cursor-pointer"
                                    title={mov?.Comentarios}>
                                    {mov?.Comentarios}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* FOOTER */}
            <div className="flex-shrink-0 bg-[#050f08] px-4 py-4 md:py-0.5 lg:py-0.5  flex items-center justify-between">
                <span className="text-gray-400 text-[12px] md:text-[10px] lg:text-[10px] uppercase tracking-widest font-bold">Pendientes de facturar</span>
                <span className="text-white font-black text-sm ">
                    {remitosPendientesFacturar?.length || 0} REGISTROS
                </span>
                {showScrollButtons && (
                    <div className="flex gap-2">
                        <button onClick={() => tableContainerRef.current.scrollTop = 0}
                            className="w-12 h-12  md:w-7 md:h-7 lg:w-7 lg:h-7 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-white transition-all">
                            <ChevronUp size={14} />
                        </button>
                        <button onClick={() => tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight}
                            className="w-12 h-12  md:w-7 md:h-7 lg:w-7 lg:h-7 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-white transition-all">
                            <ChevronDown size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}